import React, { useState, useEffect, useRef } from 'react';
import { AccessibilityPanel } from '@/components/ui/AccessibilityPanel';
import { useAccessibility } from '@/context/AccessibilityContext';
import { showToast } from '@/components/ui/Toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppAction, UserProfile as User, AppState, Role } from '@/types';
import { SubscriptionTier, FEATURE_REGISTRY } from '@everyday-os/shared';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { Button } from '@/components/ui/Button';
import { SupportTicketModal } from '@/components/ui/SupportTicketModal';
import {
  User as UserIcon, Clock, Mail, Camera, Save, Trash2, AlertTriangle,
  Smile, Coffee, Moon, Sun, Briefcase, Globe, Check, MapPin,
  Loader2, Sparkles, BookOpen, Microscope, Landmark,
  Scale, Rocket, PawPrint, Wrench, Soup, Laugh,
  Shield, Users, Layers, Lock, Info, EyeOff, Eye, Heart, LifeBuoy
} from 'lucide-react';
import { updateProfile, deleteUser } from 'firebase/auth';
import { doc, updateDoc, setDoc, getDoc, deleteDoc, serverTimestamp, arrayUnion, arrayRemove } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '@/firebase';
import { getAllTimezones, getTimezoneOffset } from '@/utils/timezoneUtils';

interface UserSettingsProps {
  user: User;
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

export const UserSettings: React.FC<UserSettingsProps> = ({ user, state, dispatch }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [googleConnecting, setGoogleConnecting] = useState(false);
  const [googleDisconnecting, setGoogleDisconnecting] = useState(false);
  const isInitialMount = useRef(true);

  // googleOAuthCallback.ts redirects back here with ?googleSync=connected|error after the
  // Connect flow completes — surface it once, then clean the query param off the URL so a
  // refresh doesn't re-show the toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleSync = params.get('googleSync');
    if (!googleSync) return;
    if (googleSync === 'connected') {
      showToast(t('settings.google_calendar_connected', 'Google Calendar connected.'), 'success');
    } else {
      showToast(t('settings.google_calendar_connect_failed', 'Could not connect Google Calendar. Please try again.'), 'error');
    }
    params.delete('googleSync');
    const newSearch = params.toString();
    navigate({ pathname: window.location.pathname, search: newSearch ? `?${newSearch}` : '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Hybrid Scope State ---
  const [preferredScope, setPreferredScope] = useState<string>(user.defaultScope || 'BOTH');

  // --- Feature Visibility State ---
  const currentHousehold = state.households?.find(h => h.id === state.currentHouseholdId);
  const householdTier = currentHousehold?.subscription?.tier ?? SubscriptionTier.FREE;
  const [hiddenFeatures, setHiddenFeatures] = useState<string[]>(user.hiddenFeatures ?? []);
  const { isAccessible } = useFeatureAccess(householdTier, hiddenFeatures);

  // --- Form States ---
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState(user.name || '');
  const [avatarUrl, setAvatarUrl] = useState(user.avatar || '');
  const [homeAddress, setHomeAddress] = useState(user.homeAddress || '');
  const [statusMessage, setStatusMessage] = useState((user as any).statusMessage || 'online');
  const [selectedLang, setSelectedLang] = useState(user.preferredLanguage || i18n.language || 'en');

  // --- Timezone State ---
  const [selectedTz, setSelectedTz] = useState(user.timezone || 'UTC');
  const [autoDetectTz, setAutoDetectTz] = useState(user.autoDetectTz !== false);

  // --- Mailing List State ---
  const [devUpdateOptIn, setDevUpdateOptIn] = useState(false);
  const [isMailingListLoading, setIsMailingListLoading] = useState(true);

  // --- Knowledge Engine State ---
  const [activeSparks, setActiveSparks] = useState<string[]>(user.activeSparkCategories || []);

  // --- Dietary Restrictions State ---
  const [dietaryRestrictions, setDietaryRestrictions] = useState<string[]>(user.dietaryRestrictions ?? []);

  const DIETARY_OPTIONS = [
    { id: 'vegan', label: 'Vegan' },
    { id: 'vegetarian', label: 'Vegetarian' },
    { id: 'gluten-free', label: 'Gluten-Free' },
    { id: 'nut-free', label: 'Nut-Free' },
    { id: 'dairy-free', label: 'Dairy-Free' },
    { id: 'halal', label: 'Halal' },
    { id: 'kosher', label: 'Kosher' },
    { id: 'low-carb', label: 'Low-Carb' },
  ];

  const toggleDietaryRestriction = async (id: string) => {
    const next = dietaryRestrictions.includes(id)
      ? dietaryRestrictions.filter(r => r !== id)
      : [...dietaryRestrictions, id];
    setDietaryRestrictions(next);
    try {
      await updateDoc(doc(db, 'users', user.id), { dietaryRestrictions: next });
      dispatch({ type: 'UPDATE_USER', payload: { dietaryRestrictions: next } as any });
    } catch (e) {
      console.error('Failed to save dietary restrictions', e);
      setDietaryRestrictions(dietaryRestrictions);
    }
  };

  // --- Elder Mode State ---
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [elderModeEnabled, setElderModeEnabled] = useState<boolean>((user as any).elderModeEnabled ?? false);
  const [elderFontSize, setElderFontSize] = useState<string>((user as any).elderFontSize ?? 'medium');

  const handleElderModeToggle = async () => {
    const newVal = !elderModeEnabled;
    setElderModeEnabled(newVal);
    await updateDoc(doc(db, 'users', user.id), { elderModeEnabled: newVal });
    dispatch({ type: 'UPDATE_USER', payload: { elderModeEnabled: newVal } as any });
  };

  const handleElderFontSizeChange = async (size: string) => {
    setElderFontSize(size);
    await updateDoc(doc(db, 'users', user.id), { elderFontSize: size });
    dispatch({ type: 'UPDATE_USER', payload: { elderFontSize: size } as any });
  };

  // --- Routine State ---
  const existingRoutines = (user as any).routines;
  const [bedtimeEnabled, setBedtimeEnabled] = useState<boolean>(existingRoutines?.bedtime?.enabled ?? false);
  const [bedtimeTarget, setBedtimeTarget] = useState<string>(existingRoutines?.bedtime?.targetTime ?? '22:00');
  const [bedtimeSteps, setBedtimeSteps] = useState<Array<{ offsetMins: number; label: string }>>(
    existingRoutines?.bedtime?.steps ?? [{ offsetMins: -30, label: 'Wind down' }]
  );
  const [isSavingRoutines, setIsSavingRoutines] = useState(false);

  const STATUS_OPTIONS = [
    { key: 'online', label: t('status.online', 'Online'), icon: Smile, color: 'text-emerald-500' },
    { key: 'at_work', label: t('status.at_work', 'At Work'), icon: Briefcase, color: 'text-blue-500' },
    { key: 'on_vacation', label: t('status.on_vacation', 'On Vacation'), icon: Sun, color: 'text-amber-500' },
    { key: 'night_shift', label: t('status.night_shift', 'Night Shift'), icon: Moon, color: 'text-blue-500' },
    { key: 'busy', label: t('status.busy', 'Busy'), icon: Coffee, color: 'text-red-500' },
  ];

const LANGUAGES = [
  { code: 'en', label: t('language.english', 'English'), flag: '🇺🇸' },
  { code: 'es', label: t('language.spanish', 'Español'), flag: '🇪🇸' },
  { code: 'fr', label: t('language.french', 'Français'), flag: '🇫🇷' }
];

const SPARK_CATEGORIES = [
  { id: 'vocabulary', label: t('sparks.vocabulary', 'Vocabulary'), icon: BookOpen, color: 'text-blue-500' },
  { id: 'animal', label: t('sparks.animal_science', 'Animal Science'), icon: PawPrint, color: 'text-emerald-500' },
  { id: 'cleaning', label: t('sparks.cleaning_tips', 'Cleaning Tips'), icon: Wrench, color: 'text-sky-500' },
  { id: 'cooking', label: t('sparks.cooking_hacks', 'Cooking Hacks'), icon: Soup, color: 'text-orange-500' },
  { id: 'history', label: t('sparks.global_history', 'Global History'), icon: Landmark, color: 'text-amber-600' },
  { id: 'jokes', label: t('sparks.dad_jokes', 'Dad Jokes'), icon: Laugh, color: 'text-pink-500' },
  { id: 'logic', label: t('sparks.logic_fallacies', 'Logic & Fallacies'), icon: Scale, color: 'text-blue-500' },
  { id: 'science', label: t('sparks.general_science', 'General Science'), icon: Microscope, color: 'text-lime-600' },
  { id: 'space', label: t('sparks.astronomy', 'Astronomy'), icon: Rocket, color: 'text-purple-500' },
  { id: 'culture', label: t('sparks.world_culture', 'World Culture'), icon: Globe, color: 'text-rose-500' },
];

  useEffect(() => {
    if (user) {
      setDisplayName(user.name);
      setAvatarUrl(user.avatar || '');
      setHomeAddress(user.homeAddress || '');
      const names = user.name.split(' ');
      setFirstName(names[0] || '');
      setLastName(names.slice(1).join(' ') || '');
      setStatusMessage((user as any).statusMessage || 'online');
      setSelectedLang(user.preferredLanguage || i18n.language || 'en');
      setSelectedTz(user.timezone || 'UTC');
      setAutoDetectTz(user.autoDetectTz !== false);
      setActiveSparks(user.activeSparkCategories && user.activeSparkCategories.length > 0
        ? user.activeSparkCategories
        : ['vocabulary'] // Default to vocabulary if none set, for existing users
      );
      setPreferredScope(user.defaultScope || 'BOTH');
      fetchMailingListStatus();
    }
  }, [user.id, user.name, user.avatar, user.preferredLanguage, user.timezone, user.autoDetectTz, user.activeSparkCategories, user.defaultScope, i18n.language]);

  const fetchMailingListStatus = async () => {
    if (!user.email) return;
    try {
      const mailRef = doc(db, "mailing_list", user.email.toLowerCase());
      const snap = await getDoc(mailRef);
      setDevUpdateOptIn(snap.exists());
    } catch (e) {
      console.error("Error fetching mailing list status:", e);
    } finally {
      setIsMailingListLoading(false);
    }
  };

  const handleMailingListToggle = async () => {
    if (!user.email) return;
    const newValue = !devUpdateOptIn;
    setDevUpdateOptIn(newValue);

    try {
      const mailRef = doc(db, "mailing_list", user.email.toLowerCase());
      if (newValue) {
        await setDoc(mailRef, {
          email: user.email.toLowerCase(),
          userId: user.id,
          optedIn: true,
          subscribedAt: serverTimestamp(),
          listType: 'DEVELOPMENT_UPDATES'
        });
      } else {
        await deleteDoc(mailRef);
      }
    } catch (e) {
      console.error("Mailing list update failed:", e);
      setDevUpdateOptIn(!newValue);
    }
  };

  const toggleSparkCategory = (id: string) => {
    setActiveSparks(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    if (autoDetectTz) {
      try {
        const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (detectedTz && detectedTz !== selectedTz) {
          setSelectedTz(detectedTz);
        }
      } catch (e) {
        console.error("Timezone detection failed:", e);
      }
    }
  }, [autoDetectTz, selectedTz]);

  useEffect(() => {
    const persistTimezonePreference = async () => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
      }
      if (!auth.currentUser || !user.id) return;
      try {
        const userRef = doc(db, "users", user.id);
        await updateDoc(userRef, {
          timezone: selectedTz,
          autoDetectTz: autoDetectTz,
          updatedAt: new Date().toISOString()
        });
      } catch (e) {
        console.error("Silent timezone save failed:", e);
      }
    };
    persistTimezonePreference();
  }, [selectedTz, autoDetectTz, user.id]);

  const handleLanguageChange = async (langCode: string) => {
    setSelectedLang(langCode);
    i18n.changeLanguage(langCode);
    if (auth.currentUser) {
      try {
        const userRef = doc(db, "users", user.id);
        await updateDoc(userRef, { preferredLanguage: langCode });
      } catch (e) {
        console.error("Language sync failed:", e);
      }
    }
  };

  const handleSave = async () => {
    if (!auth.currentUser) return;
    setIsLoading(true);
    try {
      const fullDisplayName = `${firstName} ${lastName}`.trim() || displayName;

      await updateProfile(auth.currentUser, { // Re-added Firebase Auth profile update
        displayName: fullDisplayName,
        photoURL: avatarUrl
      });

      const userRef = doc(db, "users", user.id);

      const updates = {
        name: fullDisplayName,
        avatar: avatarUrl,
        homeAddress: homeAddress,
        statusMessage: statusMessage,
        preferredLanguage: selectedLang,
        timezone: selectedTz,
        autoDetectTz: autoDetectTz,
        activeSparkCategories: activeSparks,
        defaultScope: preferredScope,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(userRef, updates);

      dispatch({
        type: 'UPDATE_USER',
        payload: updates as Partial<User>
      });

      // If they changed their default scope, sync the global state
      dispatch({ type: 'SET_DATA_SCOPE', payload: preferredScope as any });

      showToast(t('settings.profile_updated', 'Identity Synchronized.'), 'success');
    } catch (error) {
      console.error("Error updating profile:", error);
      showToast(t('settings.update_failed', 'Failed to update registry.'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveRoutines = async () => {
    if (!auth.currentUser) return;
    setIsSavingRoutines(true);
    try {
      const userRef = doc(db, 'users', user.id);
      const routinesPayload = {
        routines: {
          bedtime: {
            enabled: bedtimeEnabled,
            targetTime: bedtimeTarget,
            steps: bedtimeSteps,
          },
        },
      };
      await updateDoc(userRef, routinesPayload);
      dispatch({ type: 'UPDATE_USER', payload: routinesPayload as any });
    } catch (err) {
      console.error('Failed to save routines', err);
    } finally {
      setIsSavingRoutines(false);
    }
  };

  const handleFeatureVisibilityToggle = async (featureId: string, currentlyVisible: boolean) => {
    const userRef = doc(db, 'users', user.id);
    if (currentlyVisible) {
      const next = [...hiddenFeatures, featureId];
      setHiddenFeatures(next);
      await updateDoc(userRef, { hiddenFeatures: arrayUnion(featureId) });
      dispatch({ type: 'UPDATE_USER', payload: { hiddenFeatures: next } });
    } else {
      const next = hiddenFeatures.filter(id => id !== featureId);
      setHiddenFeatures(next);
      await updateDoc(userRef, { hiddenFeatures: arrayRemove(featureId) });
      dispatch({ type: 'UPDATE_USER', payload: { hiddenFeatures: next } });
    }
  };

  const handleDeleteAccount = async () => {
    const confirmation = prompt(t('settings.delete_confirm_prompt', 'Type DELETE to permanently remove your account:'));
    if (confirmation !== 'DELETE' || !auth.currentUser) return;
    setIsLoading(true);
    try {
      // 🛰️ Cleaning up user-level data before authentication purge
      const userRef = doc(db, "users", user.id);
      await deleteDoc(userRef);
      await deleteUser(auth.currentUser);
      dispatch({ type: 'SET_USER', payload: null });
      navigate('/');
    } catch (error: any) {
      console.error("Account deletion failed:", error);
      // More robust error handling for specific Firebase errors
      if (error.code === 'auth/requires-recent-login') {
        showToast(t('settings.delete_reauth_required', 'Please log in again to delete your account.'), 'error');
      } else {
        showToast(t('settings.delete_failed_generic', 'Failed to delete account. Please try again later.'), 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectGoogleCalendar = async () => {
    setGoogleConnecting(true);
    try {
      const fns = getFunctions();
      const initiate = httpsCallable<unknown, { url: string }>(fns, 'initiateGoogleCalendarAuth');
      const result = await initiate();
      // True top-level redirect, not a popup — only this lets Google return an authorization
      // code to our server-side callback (initiateGoogleCalendarAuth.ts / googleOAuthCallback.ts).
      window.location.href = result.data.url;
    } catch (error) {
      console.error('Failed to start Google Calendar connect flow:', error);
      showToast(t('settings.google_calendar_connect_failed', 'Could not connect Google Calendar. Please try again.'), 'error');
      setGoogleConnecting(false);
    }
  };

  const handleDisconnectGoogleCalendar = async () => {
    setGoogleDisconnecting(true);
    try {
      const fns = getFunctions();
      const disconnect = httpsCallable(fns, 'disconnectGoogleCalendar');
      await disconnect();
      // disconnectGoogleCalendar.ts writes googleOAuthAccepted:false via the Admin SDK — the
      // existing users/{uid} onSnapshot listener in App.tsx picks this up and re-dispatches
      // SET_USER automatically, so no local dispatch is needed here.
      showToast(t('settings.google_calendar_disconnected', 'Google Calendar disconnected.'), 'success');
    } catch (error) {
      console.error('Failed to disconnect Google Calendar:', error);
      showToast(t('settings.google_calendar_disconnect_failed', 'Could not disconnect Google Calendar. Please try again.'), 'error');
    } finally {
      setGoogleDisconnecting(false);
    }
  };

  return (
    <>
    <div className="pb-20 animate-in fade-in duration-500 space-y-6">

      {/* --- HEADER --- */}
      <div className="flex justify-between items-end border-b border-slate-100 dark:border-slate-800 pb-6">
        <p className="text-2xs font-black text-brand uppercase tracking-[0.3em]">
          {t('settings.subtitle', 'Identity & Regional Synchronization')}
        </p>
        <Button onClick={handleSave} className="rounded-2xl px-8 shadow-lg shadow-brand/20 bg-brand hover:bg-brand-hover text-white font-black text-xs uppercase tracking-widest h-12" disabled={isLoading}>
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {t('settings.save_button', 'Save Changes')}
        </Button>
      </div>

      {/* ROW 1 — Profile · Identity · Data Scoping */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">

        {/* Profile Card */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm text-center">
          <div className="relative inline-block mb-4">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800 border-4 border-white dark:border-slate-700 shadow-xl mx-auto">
              {avatarUrl ? <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" /> : (
                <div className="w-full h-full flex items-center justify-center text-3xl font-black text-slate-300 uppercase">
                  {firstName.charAt(0) || user.name.charAt(0)}
                </div>
              )}
            </div>
            <div className="absolute bottom-0 right-0 bg-brand text-white p-2 rounded-full shadow-lg border-2 border-white dark:border-slate-900 cursor-pointer transition-transform hover:scale-110">
              <Camera className="w-3 h-3" />
            </div>
          </div>
          <div className="space-y-3">
            <p className="text-xs font-black uppercase text-slate-400 tracking-widest">{t('settings.current_status', 'Temporal Status')}</p>
            <div className="flex flex-wrap justify-center gap-2">
              {STATUS_OPTIONS.map(status => (
                <button
                  key={status.key}
                  onClick={() => setStatusMessage(status.key)}
                  className={`px-3 py-1.5 rounded-xl text-2xs font-black uppercase tracking-widest transition-all border ${
                    statusMessage === status.key
                      ? 'bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900'
                      : 'bg-white dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700 hover:border-slate-400'
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <status.icon className={`w-3 h-3 ${statusMessage === status.key ? '' : status.color}`} />
                    {status.label}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Identity Data */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
          <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest flex items-center gap-2 italic">
            <UserIcon className="w-3 h-3 text-blue-500" /> {t('settings.personal_details', 'Identity Data')}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-2xs font-black uppercase text-slate-400 tracking-widest mb-1.5 ml-1">{t('settings.first_name', 'First Name')}</label>
              <input value={firstName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)} className="w-full p-3 bg-slate-50 dark:bg-slate-800 rounded-2xl border-none font-bold text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Alex" />
            </div>
            <div>
              <label className="block text-2xs font-black uppercase text-slate-400 tracking-widest mb-1.5 ml-1">{t('settings.last_name', 'Last Name')}</label>
              <input value={lastName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)} className="w-full p-3 bg-slate-50 dark:bg-slate-800 rounded-2xl border-none font-bold text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Doe" />
            </div>
          </div>
          <div>
            <label className="block text-2xs font-black uppercase text-slate-400 tracking-widest mb-1.5 ml-1">{t('settings.email', 'Comm Channel')}</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
              <input value={user.email} disabled className="w-full pl-10 p-3 bg-slate-100 dark:bg-slate-950 rounded-2xl border-none font-bold text-sm text-slate-500 cursor-not-allowed" />
            </div>
          </div>
          <div>
            <label className="block text-2xs font-black uppercase text-slate-400 tracking-widest mb-1.5 ml-1">{t('settings.home_address', 'Home Address')}</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
              <input
                value={homeAddress}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHomeAddress(e.target.value)}
                className="w-full pl-10 p-3 bg-slate-50 dark:bg-slate-800 rounded-2xl border-none font-bold text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                placeholder="123 Main St, City, State"
              />
            </div>
            <p className="text-[9px] font-bold text-slate-400 mt-1.5 ml-1">Used by Daily Brief for local weather. Never shared with household members.</p>
          </div>
        </div>

        {/* Data Scoping */}
        <div className="bg-slate-900 text-white p-6 rounded-[2.5rem] border border-slate-800 shadow-xl space-y-4">
          <h3 className="text-xs font-black uppercase tracking-widest flex items-center gap-2 italic">
            <Shield className="w-3 h-3 text-emerald-400" /> {t('settings.data_scoping_title', 'Data Scoping')}
          </h3>
          <p className="text-2xs font-bold text-slate-400 leading-relaxed">
            {t('settings.data_scoping_description', 'Choose how EverydayOS handles your data by default. "Hybrid" allows merged visibility of personal and shared registries.')}
          </p>
          <div className="space-y-2">
            {[
              { id: 'PERSONAL', label: t('settings.scope_personal_only', 'Personal Only'), icon: Lock, desc: '/users/{uid}' },
              { id: 'BOTH', label: t('settings.scope_hybrid', 'Hybrid (Both)'), icon: Layers, desc: 'Merged Context' },
              { id: 'SHARED', label: t('settings.scope_shared_only', 'Shared Only'), icon: Users, desc: '/households/{hid}' }
            ].map(s => (
              <button
                key={s.id}
                onClick={() => setPreferredScope(s.id)}
                className={`w-full flex items-center justify-between p-3 rounded-2xl transition-all border-2 ${
                  preferredScope === s.id ? 'bg-white/10 border-blue-500' : 'bg-white/5 border-transparent opacity-60'
                }`}
              >
                <div className="flex items-center gap-3">
                  <s.icon className={`w-4 h-4 ${preferredScope === s.id ? 'text-blue-400' : 'text-slate-500'}`} />
                  <div className="text-left">
                    <p className="text-2xs font-black uppercase">{s.label}</p>
                    <p className="text-[8px] font-bold text-slate-500">{s.desc}</p>
                  </div>
                </div>
                {preferredScope === s.id && <Check className="w-3 h-3 text-blue-400" />}
              </button>
            ))}
          </div>
          <div className="flex items-start gap-2 p-3 bg-emerald-500/10 rounded-xl">
            <Info className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" />
            <p className="text-[9px] font-bold text-emerald-400/80 leading-tight">
              {t('settings.data_scoping_verification', 'Verification: Personal data is siloed. Household members cannot access your Private Partition.')}
            </p>
          </div>
        </div>
      </div>

      {/* ROW 2 — Regional Sync · Dashboard Sparks · Customize Navigation */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">

        {/* Regional Sync */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
          <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest flex items-center gap-2 italic">
            <Globe className="w-3 h-3 text-blue-500" /> {t('settings.regional_sync_title', 'Regional Sync')}
          </h3>
          <div className="flex gap-2 flex-wrap">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all border ${
                  selectedLang.startsWith(lang.code)
                    ? 'bg-brand text-white border-brand shadow-lg shadow-brand/20'
                    : 'bg-white dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700 hover:border-brand'
                }`}
              >
                <span>{lang.flag}</span>
                <span>{lang.label}</span>
              </button>
            ))}
          </div>
          <div className="space-y-3 pt-3 border-t border-slate-100 dark:border-slate-800">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest flex items-center gap-2 italic">
                <Clock className="w-3 h-3 text-blue-500" /> {t('settings.timezone_title', 'Timezone')}
              </h3>
              <label className="flex items-center gap-2 cursor-pointer group">
                <span className="text-[9px] font-black uppercase text-slate-400 group-hover:text-blue-500 transition-colors">
                  {autoDetectTz ? t('settings.timezone_automatic', 'Automatic') : t('settings.timezone_manual', 'Manual')}
                </span>
                <div className="relative">
                  <input type="checkbox" className="sr-only peer" checked={autoDetectTz} onChange={(e) => setAutoDetectTz(e.target.checked)} />
                  <div className="w-8 h-4 bg-slate-200 dark:bg-slate-700 rounded-full peer peer-checked:bg-brand after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-4"></div>
                </div>
              </label>
            </div>
            <div className="relative">
              <select
                disabled={autoDetectTz}
                value={selectedTz}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedTz(e.target.value)}
                className={`w-full p-3 rounded-2xl bg-slate-50 dark:bg-slate-800 border-none font-bold text-xs text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 transition-all ${autoDetectTz ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {getAllTimezones().map(tz => (
                  <option key={tz} value={tz}>{tz.replace(/_/g, ' ')} ({getTimezoneOffset(tz)})</option>
                ))}
              </select>
              {autoDetectTz && <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-blue-500 animate-pulse" />}
            </div>
          </div>
        </div>

        {/* Dashboard Sparks */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
          <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest flex items-center gap-2 italic">
            <Sparkles className="w-3 h-3 text-blue-500" /> {t('settings.dashboard_sparks', 'Dashboard Sparks')}
          </h3>
          <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto pr-1 no-scrollbar">
            {SPARK_CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => toggleSparkCategory(cat.id)}
                className={`flex items-center justify-between p-3 rounded-2xl transition-all border-2 ${
                  activeSparks.includes(cat.id)
                    ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800'
                    : 'bg-slate-50 dark:bg-slate-800/50 border-transparent text-slate-400'
                }`}
              >
                <div className="flex items-center gap-3">
                  <cat.icon className={`w-4 h-4 ${activeSparks.includes(cat.id) ? cat.color : 'text-slate-300'}`} />
                  <span className={`text-[11px] font-black uppercase tracking-wider ${activeSparks.includes(cat.id) ? 'text-slate-900 dark:text-white' : ''}`}>
                    {cat.label}
                  </span>
                </div>
                <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${activeSparks.includes(cat.id) ? 'bg-brand text-white' : 'bg-slate-200 dark:bg-slate-700'}`}>
                  {activeSparks.includes(cat.id) && <Check className="w-3 h-3" />}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Customize Navigation */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <EyeOff className="w-3 h-3 text-blue-500" />
              <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest italic">Customize Navigation</h3>
            </div>
            <p className="text-2xs font-bold text-slate-400 uppercase tracking-tight">
              Hide features you don't use. Re-enable them here anytime.
            </p>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto no-scrollbar">
            {FEATURE_REGISTRY.filter(f => f.hideable && isAccessible(f.id)).map(feature => {
              const isVisible = !hiddenFeatures.includes(feature.id);
              return (
                <div key={feature.id} className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/50">
                  <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{feature.label}</span>
                  <button
                    onClick={() => handleFeatureVisibilityToggle(feature.id, isVisible)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      isVisible ? 'bg-brand' : 'bg-slate-300 dark:bg-slate-600'
                    }`}
                    aria-label={isVisible ? `Hide ${feature.label}` : `Show ${feature.label}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                      isVisible ? 'translate-x-4' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
              );
            })}
            {FEATURE_REGISTRY.filter(f => f.hideable && isAccessible(f.id)).length === 0 && (
              <p className="text-sm text-slate-400 text-center py-4">No hideable features available for your plan.</p>
            )}
          </div>
        </div>
      </div>

      {/* ROW 3 — Bedtime Routine · Accessibility & Display */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">

        {/* Bedtime Routine */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Moon className="w-3 h-3 text-blue-500" />
              <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest italic">Bedtime Routine</h3>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-[9px] font-black uppercase text-slate-400">
                {bedtimeEnabled ? 'Enabled' : 'Disabled'}
              </span>
              <div className="relative">
                <input type="checkbox" className="sr-only peer" checked={bedtimeEnabled} onChange={e => setBedtimeEnabled(e.target.checked)} />
                <div className="w-8 h-4 bg-slate-200 dark:bg-slate-700 rounded-full peer peer-checked:bg-brand after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-4" />
              </div>
            </label>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-2xs font-black uppercase text-slate-400 tracking-widest whitespace-nowrap">Target Time</label>
            <input
              type="time"
              value={bedtimeTarget}
              onChange={e => setBedtimeTarget(e.target.value)}
              disabled={!bedtimeEnabled}
              className="flex-1 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800 border-none font-bold text-xs text-slate-700 dark:text-slate-200 disabled:opacity-50"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-2xs font-black uppercase text-slate-400 tracking-widest">Steps</span>
              <button
                onClick={() => setBedtimeSteps(prev => [...prev, { offsetMins: -15, label: 'New step' }])}
                disabled={!bedtimeEnabled}
                className="text-[9px] font-black uppercase text-blue-500 tracking-widest hover:text-blue-700 disabled:opacity-50"
              >
                + Add Step
              </button>
            </div>
            {bedtimeSteps.map((step, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  type="number"
                  value={step.offsetMins}
                  onChange={e => setBedtimeSteps(prev => prev.map((s, i) => i === idx ? { ...s, offsetMins: parseInt(e.target.value) || 0 } : s))}
                  disabled={!bedtimeEnabled}
                  className="w-16 p-2 rounded-xl bg-slate-50 dark:bg-slate-800 border-none font-bold text-xs text-center disabled:opacity-50"
                  placeholder="-30"
                />
                <span className="text-[9px] text-slate-400 font-bold">min</span>
                <input
                  type="text"
                  value={step.label}
                  onChange={e => setBedtimeSteps(prev => prev.map((s, i) => i === idx ? { ...s, label: e.target.value } : s))}
                  disabled={!bedtimeEnabled}
                  className="flex-1 p-2 rounded-xl bg-slate-50 dark:bg-slate-800 border-none font-bold text-xs disabled:opacity-50"
                  placeholder="Step label"
                />
                <button
                  onClick={() => setBedtimeSteps(prev => prev.filter((_, i) => i !== idx))}
                  disabled={!bedtimeEnabled}
                  className="text-slate-300 hover:text-red-500 transition-colors disabled:opacity-50"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={handleSaveRoutines}
            disabled={isSavingRoutines}
            className="w-full h-10 rounded-2xl bg-brand hover:bg-brand-hover text-white font-black uppercase tracking-widest text-xs transition-colors disabled:opacity-50"
          >
            {isSavingRoutines ? 'Saving...' : 'Save Routine'}
          </button>
        </div>

        {/* Accessibility & Display (spans 2 columns) */}
        <div className="md:col-span-1 xl:col-span-2 bg-white dark:bg-slate-900 p-6 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
          <div className="flex items-center gap-2">
            <Heart className="w-3 h-3 text-rose-500" />
            <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest italic">Accessibility &amp; Display</h3>
          </div>
          <AccessibilityPanel />
          <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
            <div className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/50">
              <div>
                <p className="text-sm font-bold text-slate-700 dark:text-slate-300">Elder Mode</p>
                <p className="text-2xs font-bold text-slate-400 mt-0.5">Simplified high-contrast interface with large text and one-tap actions</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <div className="relative">
                  <input type="checkbox" className="sr-only peer" checked={elderModeEnabled} onChange={handleElderModeToggle} />
                  <div className="w-8 h-4 bg-slate-200 dark:bg-slate-700 rounded-full peer peer-checked:bg-rose-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-4"></div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* ROW 4 — Dietary Restrictions */}
      <div className="bg-white dark:bg-slate-900 p-6 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
        <div>
          <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest flex items-center gap-2 italic">
            <Soup className="w-3 h-3 text-orange-500" /> Dietary Restrictions
          </h3>
          <p className="text-2xs font-bold text-slate-400 mt-1">
            Used to flag conflicts when planning meals with household members.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {DIETARY_OPTIONS.map(opt => {
            const active = dietaryRestrictions.includes(opt.id);
            return (
              <button
                key={opt.id}
                onClick={() => toggleDietaryRestriction(opt.id)}
                className={`px-4 py-2 rounded-2xl text-xs font-black uppercase tracking-widest transition-all border-2 ${
                  active
                    ? 'bg-orange-500 border-orange-500 text-white shadow-md shadow-orange-500/20'
                    : 'bg-slate-50 dark:bg-slate-800 border-transparent text-slate-500 hover:border-orange-300 dark:hover:border-orange-700'
                }`}
              >
                {active && <Check className="inline w-3 h-3 mr-1 -mt-0.5" />}
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* BOTTOM ROW — Get Help · Purge Registry */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Get Help */}
        {/* Connected Accounts — Google Calendar */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-2xl ${user.googleOAuthAccepted ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-slate-100 dark:bg-slate-800'}`}>
              <Globe className={`w-5 h-5 ${user.googleOAuthAccepted ? 'text-emerald-500' : 'text-slate-400'}`} />
            </div>
            <div>
              <h3 className="text-xs font-black uppercase text-slate-700 dark:text-white tracking-widest">
                {t('settings.google_calendar_title', 'Google Calendar')}
              </h3>
              <p className="text-2xs font-bold text-slate-400 mt-0.5">
                {user.googleOAuthAccepted
                  ? t('settings.google_calendar_connected_desc', 'Connected — your Google Calendar events sync in automatically.')
                  : t('settings.google_calendar_disconnected_desc', 'Connect to sync your Google Calendar events into EverydayOS.')}
              </p>
            </div>
          </div>
          {user.googleOAuthAccepted ? (
            <button
              onClick={handleDisconnectGoogleCalendar}
              disabled={googleDisconnecting}
              className="shrink-0 px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-black uppercase tracking-widest rounded-xl transition-colors disabled:opacity-50"
            >
              {googleDisconnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('settings.disconnect', 'Disconnect')}
            </button>
          ) : (
            <button
              onClick={handleConnectGoogleCalendar}
              disabled={googleConnecting}
              className="shrink-0 px-4 py-2 bg-brand hover:bg-brand-hover text-white text-xs font-black uppercase tracking-widest rounded-xl transition-colors disabled:opacity-50"
            >
              {googleConnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('settings.connect', 'Connect')}
            </button>
          )}
        </div>

        <div className="bg-white dark:bg-slate-900 p-6 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-2xl">
              <LifeBuoy className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h3 className="text-xs font-black uppercase text-slate-700 dark:text-white tracking-widest">Get Help</h3>
              <p className="text-2xs font-bold text-slate-400 mt-0.5">Report a bug, request a feature, or get account support.</p>
            </div>
          </div>
          <button
            onClick={() => setShowSupportModal(true)}
            className="shrink-0 px-4 py-2 bg-brand hover:bg-brand-hover text-white text-xs font-black uppercase tracking-widest rounded-xl transition-colors"
          >
            Contact Support
          </button>
        </div>

        {/* Purge Registry */}
        <div className="bg-red-50 dark:bg-red-900/10 p-6 rounded-[2.5rem] border border-red-100 dark:border-red-900/30 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-red-100 dark:bg-red-900/40 rounded-2xl shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-sm font-black text-red-900 dark:text-red-400 uppercase italic leading-none">{t('settings.delete_title', 'Purge Registry')}</h3>
              <p className="text-2xs font-black uppercase text-red-700 dark:text-red-300/80 mt-1 tracking-tighter">{t('settings.delete_description', 'Deletion is irreversible. All household data associated with this node will be shredded.')}</p>
            </div>
          </div>
          <Button onClick={handleDeleteAccount} className="shrink-0 h-10 rounded-xl font-black uppercase tracking-widest bg-red-600 hover:bg-red-700 text-white transition-all shadow-lg shadow-red-500/20 text-xs px-5">
            <Trash2 className="w-4 h-4 mr-2" /> {t('settings.delete_button', 'Delete')}
          </Button>
        </div>
      </div>

    </div>

    {showSupportModal && (
      <SupportTicketModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        state={state}
      />
    )}
    </>
  );
};